import { useMutation } from '@tanstack/react-query';

import type {
  BasicDataSchema,
  DescriptionSchema,
  DetailsSchema,
  EnergyDetailsSchema,
} from '@/features/project-details/schemas/details';
import type { MutationConfig } from '@/lib/react-query';
import { api, extractData } from '@/lib/treaty';

type ProjectDetails = DetailsSchema['details'];

export interface UpdateProjectDetails extends ProjectDetails {
  id: string;
}

export interface UpdateProjectDescription extends DescriptionSchema {
  id: string;
}

export interface UpdateProjectAreaDetails
  extends Pick<
    Parameters<ReturnType<typeof api.projects>['areas']['put']>[0],
    | 'hasElevator'
    | 'hasLockerRoom'
    | 'numberOfFloors'
    | 'numberOfParkingSpaces'
    | 'numberOfUnits'
    | 'parkingSpacesType'
  > {
  id: string;
}

export interface UpdateProjectEnergyCertificate extends EnergyDetailsSchema {
  id: string;
}

export interface UpdateProjectBasicData extends BasicDataSchema {
  id: string;
}

// --- project details ---
type UseUpdateProjectDetails = {
  mutationConfig?: MutationConfig<typeof updateProjectDetails>;
};

export const updateProjectDetails = ({
  id,
  ...args
}: UpdateProjectDetails | UpdateProjectDescription) => {
  return extractData(api.projects({ id }).details.put(args));
};

export const useUpdateProjectDetails = ({ mutationConfig }: UseUpdateProjectDetails) =>
  useMutation({
    ...mutationConfig,
    mutationFn: updateProjectDetails,
  });

// --- project area room ---
type UseUpdateProjectAreaDetails = {
  mutationConfig?: MutationConfig<typeof updateProjectAreaDetails>;
};

export const updateProjectAreaDetails = ({ id, ...args }: UpdateProjectAreaDetails) => {
  return extractData(api.projects({ id }).areas.put(args));
};

export const useUpdateProjectAreaDetails = ({ mutationConfig }: UseUpdateProjectAreaDetails) =>
  useMutation({
    ...mutationConfig,
    mutationFn: updateProjectAreaDetails,
  });

// --- project energy certificate ---
type UseUpdateProjectEnergyCertificate = {
  mutationConfig?: MutationConfig<typeof updateProjectEnergyCertificate>;
};

export const updateProjectEnergyCertificate = ({ id, ...args }: UpdateProjectEnergyCertificate) => {
  return extractData(api.projects({ id }).energy.put(args));
};

export const useUpdateProjectEnergyCertificate = ({
  mutationConfig,
}: UseUpdateProjectEnergyCertificate) =>
  useMutation({
    ...mutationConfig,
    mutationFn: updateProjectEnergyCertificate,
  });

// --- project basic data ---
type UseUpdateProjectBasicData = {
  mutationConfig?: MutationConfig<typeof updateProjectBasicData>;
};

export const updateProjectBasicData = ({ id, ...args }: UpdateProjectBasicData) => {
  return extractData(api.projects({ id }).put(args));
};

export const useUpdateProjectBasicData = ({ mutationConfig }: UseUpdateProjectBasicData) =>
  useMutation({
    ...mutationConfig,
    mutationFn: updateProjectBasicData,
  });
